import {
  type PdfCustomLayout,
  type PdfExtraLogo,
  defaultPdfCustomLayout,
  parsePdfLayoutJson
} from './pdfCustomLayout';

export type PdfDocType = 'quote' | 'invoice' | 'inspection';

/** Deep-partial of a `PdfCustomLayout` — used for override stacks where any subset of fields may be set. */
export type LayoutPatch = {
  v?: 1;
  logo?: Partial<PdfCustomLayout['logo']>;
  badges?: Partial<PdfCustomLayout['badges']>;
  stripe?: Partial<NonNullable<PdfCustomLayout['stripe']>>;
  invoiceHeader?: Partial<PdfCustomLayout['invoiceHeader']>;
  colors?: Partial<PdfCustomLayout['colors']>;
  type?: Partial<PdfCustomLayout['type']>;
  body?: Partial<NonNullable<PdfCustomLayout['body']>>;
  footer?: Partial<NonNullable<PdfCustomLayout['footer']>>;
  extras?: PdfCustomLayout['extras'];
  extraOrder?: string[];
};

export type PdfLayoutJsonOverrides = Partial<Record<PdfDocType, LayoutPatch>>;

export type PerDocStringMap = Partial<Record<PdfDocType, string>>;
export type PerDocBoolMap = Partial<Record<PdfDocType, boolean>>;

export type PdfStudioBranding = {
  companyName?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  hstNumber?: string;
  brandColor?: string;
  logoBase64?: string;
  cfaaLogoBase64?: string;
  acmoLogoBase64?: string;
  pdfTemplate?: 'generic' | 'premium' | 'zera';
  pdfVariant?: 'premium' | 'generic';
  disclaimerText?: string;
  complianceText?: string;
  pdfLayoutJson?: string;
  pdfLayoutJsonOverrides?: PdfLayoutJsonOverrides;
  useClientRasterExport?: boolean;
  useBundledAssociationBadges?: boolean;
  /** Per-doc custom HTML body (overrides managed layout when `useCustomPdf[type]` is true). */
  customPdfHtml?: PerDocStringMap;
  /** Per-doc companion CSS for the custom HTML. */
  customPdfCss?: PerDocStringMap;
  /** Per-doc toggle to actually use the custom HTML at render time. */
  useCustomPdf?: PerDocBoolMap;
};

/** Merge a layout patch onto a full layout, producing a full layout (used at render time). */
export function mergePdfLayoutPartial(
  base: PdfCustomLayout,
  patch: LayoutPatch | undefined
): PdfCustomLayout {
  if (!patch) return base;
  return {
    v: 1,
    logo: { ...base.logo, ...patch.logo },
    badges: { ...base.badges, ...patch.badges },
    stripe: { ...(base.stripe || {}), ...(patch.stripe || {}) },
    invoiceHeader: { ...base.invoiceHeader, ...patch.invoiceHeader },
    colors: { ...base.colors, ...patch.colors },
    type: { ...base.type, ...patch.type },
    body: { ...(base.body || {}), ...(patch.body || {}) },
    footer: { ...(base.footer || {}), ...(patch.footer || {}) },
    extras: patch.extras !== undefined ? patch.extras : base.extras,
    extraOrder: patch.extraOrder !== undefined ? patch.extraOrder : base.extraOrder
  };
}

/** Merge two layout patches, deep-merging subobjects — used for override-over-override edits. */
export function mergeLayoutPatches(a: LayoutPatch | undefined, b: LayoutPatch | undefined): LayoutPatch {
  const aSafe = a || {};
  const bSafe = b || {};
  return {
    v: 1,
    logo: aSafe.logo || bSafe.logo ? { ...(aSafe.logo || {}), ...(bSafe.logo || {}) } : undefined,
    badges:
      aSafe.badges || bSafe.badges ? { ...(aSafe.badges || {}), ...(bSafe.badges || {}) } : undefined,
    stripe:
      aSafe.stripe || bSafe.stripe ? { ...(aSafe.stripe || {}), ...(bSafe.stripe || {}) } : undefined,
    invoiceHeader:
      aSafe.invoiceHeader || bSafe.invoiceHeader
        ? { ...(aSafe.invoiceHeader || {}), ...(bSafe.invoiceHeader || {}) }
        : undefined,
    colors:
      aSafe.colors || bSafe.colors ? { ...(aSafe.colors || {}), ...(bSafe.colors || {}) } : undefined,
    type: aSafe.type || bSafe.type ? { ...(aSafe.type || {}), ...(bSafe.type || {}) } : undefined,
    body: aSafe.body || bSafe.body ? { ...(aSafe.body || {}), ...(bSafe.body || {}) } : undefined,
    footer:
      aSafe.footer || bSafe.footer ? { ...(aSafe.footer || {}), ...(bSafe.footer || {}) } : undefined,
    extras: bSafe.extras !== undefined ? bSafe.extras : aSafe.extras,
    extraOrder: bSafe.extraOrder !== undefined ? bSafe.extraOrder : aSafe.extraOrder
  };
}

export function resolveStudioLayout(branding: PdfStudioBranding, docType: PdfDocType): PdfCustomLayout {
  const global = parsePdfLayoutJson(branding.pdfLayoutJson, branding.brandColor || '#2d8dcc');
  const override = branding.pdfLayoutJsonOverrides?.[docType];
  return mergePdfLayoutPartial(global, override);
}

export function layoutToJsonString(layout: PdfCustomLayout): string {
  return JSON.stringify(layout);
}

export function readGlobalLayout(branding: PdfStudioBranding): PdfCustomLayout {
  return parsePdfLayoutJson(branding.pdfLayoutJson, branding.brandColor || '#2d8dcc');
}

export function readDocOverride(branding: PdfStudioBranding, docType: PdfDocType): LayoutPatch {
  return branding.pdfLayoutJsonOverrides?.[docType] ?? {};
}

export function setGlobalLayout(
  branding: PdfStudioBranding,
  layout: PdfCustomLayout
): PdfStudioBranding {
  return { ...branding, pdfLayoutJson: layoutToJsonString(layout) };
}

export function setDocOverride(
  branding: PdfStudioBranding,
  docType: PdfDocType,
  patch: LayoutPatch
): PdfStudioBranding {
  const overrides = { ...(branding.pdfLayoutJsonOverrides || {}) };
  overrides[docType] = mergeLayoutPatches(overrides[docType], patch);
  return { ...branding, pdfLayoutJsonOverrides: overrides };
}

/** Path of a leaf field inside a `LayoutPatch` (e.g. `"colors.headerBg"`, `"type.docTitlePx"`). */
export type OverrideFieldPath =
  | `logo.${keyof PdfCustomLayout['logo']}`
  | `badges.${keyof PdfCustomLayout['badges']}`
  | `stripe.${keyof NonNullable<PdfCustomLayout['stripe']>}`
  | `invoiceHeader.${keyof PdfCustomLayout['invoiceHeader']}`
  | `colors.${keyof PdfCustomLayout['colors']}`
  | `type.${keyof PdfCustomLayout['type']}`
  | `body.${keyof NonNullable<PdfCustomLayout['body']>}`
  | `footer.${keyof NonNullable<PdfCustomLayout['footer']>}`
  | 'extras'
  | 'extraOrder';

/**
 * Remove a single override field from a per-document patch.
 * If the containing section becomes empty, drop the section.
 * If the patch itself becomes empty, drop the document key entirely.
 */
export function clearOverrideField(
  overrides: PdfLayoutJsonOverrides,
  docType: PdfDocType,
  path: OverrideFieldPath
): PdfLayoutJsonOverrides {
  const current = overrides[docType];
  if (!current) return overrides;
  const next = { ...current } as Record<string, unknown>;
  if (path === 'extras' || path === 'extraOrder') {
    delete next[path];
  } else {
    const [section, field] = path.split('.') as [string, string];
    const sub = next[section];
    if (sub && typeof sub === 'object') {
      const subNext = { ...(sub as Record<string, unknown>) };
      delete subNext[field];
      if (Object.keys(subNext).length === 0) {
        delete next[section];
      } else {
        next[section] = subNext;
      }
    }
  }
  const outOverrides = { ...overrides };
  if (Object.keys(next).length === 0 || (Object.keys(next).length === 1 && next.v === 1)) {
    delete outOverrides[docType];
  } else {
    outOverrides[docType] = next as LayoutPatch;
  }
  return outOverrides;
}

/** Detect whether a per-document patch has a particular field set. */
export function hasOverrideField(
  patch: LayoutPatch | undefined,
  path: OverrideFieldPath
): boolean {
  if (!patch) return false;
  if (path === 'extras' || path === 'extraOrder') {
    return (patch as Record<string, unknown>)[path] !== undefined;
  }
  const [section, field] = path.split('.') as [string, string];
  const sub = (patch as Record<string, unknown>)[section];
  if (!sub || typeof sub !== 'object') return false;
  return (sub as Record<string, unknown>)[field] !== undefined;
}

// =====================================================================
// Export / Import — backup & share Studio config
// =====================================================================

export type PdfStudioExport = {
  version: 1;
  exportedAt: string;
  branding: PdfStudioBranding;
};

const EXPORT_FIELDS_LOGO = ['logoBase64', 'cfaaLogoBase64', 'acmoLogoBase64'] as const;

/** Build a JSON-serializable snapshot of the Studio configuration. */
export function exportStudioConfig(
  branding: PdfStudioBranding,
  opts: { includeLogos?: boolean } = {}
): PdfStudioExport {
  const out: PdfStudioBranding = { ...branding };
  if (!opts.includeLogos) {
    for (const k of EXPORT_FIELDS_LOGO) {
      if (k in out) delete (out as Record<string, unknown>)[k];
    }
  }
  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    branding: out
  };
}

/** Parse and validate a Studio export JSON file. Returns null on parse/version mismatch. */
export function parseStudioImport(json: string): PdfStudioBranding | null {
  let data: unknown;
  try {
    data = JSON.parse(json);
  } catch {
    return null;
  }
  if (!data || typeof data !== 'object') return null;
  const rec = data as Record<string, unknown>;
  if (rec.version !== 1) return null;
  const branding = rec.branding;
  if (!branding || typeof branding !== 'object') return null;
  return branding as PdfStudioBranding;
}

/** Trigger a browser download of `data` as `filename`. */
export function downloadFile(filename: string, data: string, mime = 'application/json'): void {
  const blob = new Blob([data], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function newExtraLogo(existing: PdfExtraLogo[]): PdfExtraLogo {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `extra-${existing.length}-${Date.now()}`;
  return {
    id,
    dataUrl: '',
    xFrac: 0.72,
    yFrac: 0.5,
    slotWidthFrac: 0.14
  };
}

export function defaultBranding(): PdfStudioBranding {
  const brandColor = '#2d8dcc';
  return {
    brandColor,
    pdfTemplate: 'generic',
    pdfVariant: 'generic',
    pdfLayoutJson: layoutToJsonString(defaultPdfCustomLayout(brandColor)),
    pdfLayoutJsonOverrides: {}
  };
}
